import { Controller } from "@hotwired/stimulus"

// Anmeldeaktivität heartbeat (see app/services/login_activity.rb). Every `interval` it tells
// the server whether someone used this tab within `idleThreshold`; while nobody does, it only
// sends a passive beat every `presenceInterval` to say the tab is still open.
//
// Rendered with data-turbo-permanent, so it survives Turbo visits. The last input is also kept
// in sessionStorage, because the click that triggers a full page load would otherwise be lost
// with the old page.
//
// Input is recorded at most every INPUT_THROTTLE_MS: scrolling and typing fire dozens of events a
// second, each a synchronous sessionStorage write, and the idle threshold is minutes anyway.
const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll", "mousemove"]
const STORAGE_KEY = "activity-heartbeat:last-input-at"
const INPUT_THROTTLE_MS = 5000

export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 60000 },
    presenceInterval: { type: Number, default: 300000 },
    idleThreshold: { type: Number, default: 300000 },
  }

  connect() {
    this.lastInputAt = this.readStoredInput()
    this.lastSentAt = 0
    this.recordInput = this.recordInput.bind(this)
    INPUT_EVENTS.forEach((type) =>
      document.addEventListener(type, this.recordInput, { capture: true, passive: true }),
    )
    this.timer = setInterval(() => this.tick(), this.intervalValue)
    this.tick({ force: true })
  }

  disconnect() {
    INPUT_EVENTS.forEach((type) =>
      document.removeEventListener(type, this.recordInput, { capture: true }),
    )
    clearInterval(this.timer)
  }

  recordInput() {
    if (Date.now() - this.lastInputAt < INPUT_THROTTLE_MS) return

    this.lastInputAt = Date.now()
    try {
      sessionStorage.setItem(STORAGE_KEY, String(this.lastInputAt))
    } catch {
      // Private mode or blocked storage: the in-memory value still works for this page.
    }
  }

  tick({ force = false } = {}) {
    const now = Date.now()
    const active =
      document.visibilityState === "visible" && now - this.lastInputAt < this.idleThresholdValue

    if (active || force || now - this.lastSentAt >= this.presenceIntervalValue) this.send(active)
  }

  async send(active) {
    this.lastSentAt = Date.now()
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        // Accept JSON so Devise answers a timed-out session with 401 instead of a redirect.
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ active }),
      })
      // Signed out or timed out: nothing to report any more until the next page load.
      if (response.status === 401) clearInterval(this.timer)
    } catch {
      // Offline or the server is restarting; the next beat tries again.
    }
  }

  readStoredInput() {
    try {
      return Number(sessionStorage.getItem(STORAGE_KEY)) || 0
    } catch {
      return 0
    }
  }
}
